// Что с объектами: правая колонка «Объектов», когда объект ещё не выбран.
//
// ## Почему здесь вообще что-то стоит
//
// При 1680 точках ширины колонка детали — 1024 точки, и до 04.09.2026 в ней стоял абзац по
// центру: «Объект не выбран. Выберите объект в списке слева». Девять десятых площади белые, и
// сказано в них ровно то, что человек и так видит.
//
// Образец, как надо, в продукте уже есть дважды: на пульте подотчёта вместо «выберите
// человека» стоит сводка за период, а в «Людях» — состав реестра. Здесь тот же приём, но
// вопрос другой. Человек, открывающий «Объекты», спрашивает не «какие они бывают», а
// **где не хватает людей**: это первый вопрос операционного руководителя, и он же — один из
// четырнадцати вопросов, на которые отвечает помощник.
//
// ## Почему числа с сервера, а не посчитаны на месте
//
// Укомплектованность считается не по строкам списка: в них нет ни норматива по постам, ни
// живых назначений на сегодня. Тот же отчёт показывают «Показатели», и второй его расчёт на
// экране объектов разъехался бы с первым — сегодня же, на первой правке норматива.
//
// ## Кому её не видно
//
// Отчёт требует своего права. У кого его нет — видит прежнее «Объект не выбран»: пустая
// колонка честнее выдуманного содержимого.

import { RiAddLine } from '@remixicon/react';
import { Link } from 'react-router';

import type { StaffingOut } from '@/shared/api/endpoints/reports';
import { countLabel } from '@/shared/text/plural';
import * as Button from '@/shared/ui/button';
import * as ProgressBar from '@/shared/ui/progress-bar';

const ОБЪЕКТЫ = ['объект', 'объекта', 'объектов'] as const;
/** Тот же объект, но в ответе на вопрос «где»: «на одном объекте», «на пяти объектах». */
const ОБЪЕКТЫ_ГДЕ = ['объекте', 'объектах', 'объектах'] as const;
const ЧЕЛОВЕКИ = ['человека', 'человек', 'человек'] as const;

/** Сколько объектов с нехваткой показываем списком. Дальше это уже не сводка, а второй реестр. */
const СКОЛЬКО_ПОКАЗЫВАЕМ = 7;

export interface ЧтоСОбъектамиProps {
  сводка: StaffingOut;
  /** Куда ведёт строка объекта: адрес его карточки. */
  ссылка: (siteId: string) => string;
  /** Кнопка заведения объекта — только тому, у кого есть право. */
  заведение: string | null;
}

export function ЧтоСОбъектами({ сводка, ссылка, заведение }: ЧтоСОбъектамиProps) {
  const нехватка = сводка.items
    .filter((строка) => строка.deficit > 0)
    .sort((слева, справа) => справа.deficit - слева.deficit);

  return (
    <div className='flex h-full max-w-metrics flex-col gap-6 overflow-y-auto p-6'>
      <div>
        <h2 className='text-label-lg text-text-strong-950'>
          {сводка.total_deficit > 0 ? 'Где не хватает людей' : 'Объекты укомплектованы'}
        </h2>
        {/* Пары «назначено N из M» здесь НЕТ намеренно.
            На стенде она читается как «288 из 145» и выглядит ошибкой расчёта: люди назначены
            на объекты, где посты уже закрыты, а не хватает на других. Общая сумма по всем
            объектам не отвечает ни на один вопрос человека — а вопрос у него один: где искать
            людей и скольких. */}
        <p className='mt-1 text-paragraph-sm text-text-sub-600'>
          {сводка.total_deficit > 0
            ? `Не хватает ${countLabel(сводка.total_deficit, ЧЕЛОВЕКИ)} на ${countLabel(
                сводка.understaffed_sites,
                ОБЪЕКТЫ_ГДЕ,
              )}.`
            : 'На сегодня закрыты все посты: людей хватает везде.'}
        </p>
      </div>

      {нехватка.length > 0 && (
        <ul className='flex flex-col gap-3'>
          {нехватка.slice(0, СКОЛЬКО_ПОКАЗЫВАЕМ).map((строка) => (
            <li key={строка.site_id}>
              <Link
                to={ссылка(строка.site_id)}
                className='group flex flex-col gap-1.5 rounded-lg px-2 py-1.5 hover:bg-bg-weak-50'
              >
                <span className='flex items-baseline justify-between gap-3'>
                  <span className='truncate text-paragraph-sm text-text-strong-950'>
                    {строка.site_name}
                  </span>
                  {/* Не хватает — это ЧИСЛО ЛЮДЕЙ, а не доля: доля («укомплектован на 80%»)
                      не говорит, скольких искать, а искать надо именно людей. */}
                  <span className='shrink-0 tabular-nums text-label-sm text-error-base'>
                    −{строка.deficit}
                  </span>
                </span>
                <span className='flex items-center gap-2'>
                  {/* Полоса показывает УКОМПЛЕКТОВАННОСТЬ, а не нехватку.
                      Полосу читают как «сколько сделано»: полная — хорошо. Полоса нехватки
                      читалась бы наоборот — у худшего объекта она была бы длиннее всех, и
                      глаз получал бы «здесь лучше всего». Первый кадр это и показал. */}
                  <ProgressBar.Root
                    value={строка.required === 0 ? 0 : (строка.assigned / строка.required) * 100}
                    className='h-1 flex-1'
                  />
                  <span className='shrink-0 text-paragraph-xs text-text-sub-600 tabular-nums'>
                    {строка.assigned} из {строка.required}
                  </span>
                </span>
              </Link>
            </li>
          ))}
          {нехватка.length > СКОЛЬКО_ПОКАЗЫВАЕМ && (
            <li className='px-2 text-paragraph-xs text-text-sub-600'>
              и ещё {countLabel(нехватка.length - СКОЛЬКО_ПОКАЗЫВАЕМ, ОБЪЕКТЫ)} с нехваткой
            </li>
          )}
        </ul>
      )}

      {заведение !== null && (
        <div>
          <Button.Root variant='neutral' mode='stroke' size='xsmall' asChild>
            <Link to={заведение}>
              <Button.Icon as={RiAddLine} />
              Завести объект
            </Link>
          </Button.Root>
        </div>
      )}
    </div>
  );
}
